/**
 * PATH: apps/quote/src/pages/claims/claim-editor-page/ClaimEditorPage.tsx
 * FILE_ID: QUOTE-CLAIMS-EDITOR-FRAME-001
 * PURPOSE: Thin orchestrator for the G10 claim editor workspaces.
 */
import { useEffect } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { AlertCircle, Loader2 } from 'lucide-react'
import ClaimSummary from './ClaimSummary'
import RecipientPickerDialog from './RecipientPickerDialog'
import { ClaimEvidencePanel, useClaimEvidence } from './evidence'
import { useQuoteTabStore } from '../../../config/tabs'
import { ClaimAssessmentWorkspace } from './ClaimAssessmentWorkspace'
import { ClaimEditorHeader } from './ClaimEditorHeader'
import { ClaimLinesWorkspace } from './ClaimLinesWorkspace'
import { ClaimPermissionDenied } from './ClaimPermissionDenied'
import { useClaimDocumentActions } from './useClaimDocumentActions'
import { useClaimEditorController } from './useClaimEditorController'

export default function ClaimEditorPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const location = useLocation()
  const updateActiveTab = useQuoteTabStore((state) => state.updateActiveTab)
  const controller = useClaimEditorController(id)
  const actions = useClaimDocumentActions(controller)
  const evidence = useClaimEvidence(id)
  const sequence = (
    controller.claim as (typeof controller.claim & {
      contract_sequence?: number | null
    })
  )?.contract_sequence ?? controller.claim?.claim_number

  useEffect(() => {
    if (sequence) updateActiveTab(location.pathname, `Claim #${sequence}`)
    if (new URLSearchParams(location.search).get('send') === 'true') actions.openRecipient()
    if (new URLSearchParams(location.search).get('workspace') === 'assessment') controller.setView('assessment')
    // The controller objects are intentionally not dependencies: their identities
    // change on render while the individual React callbacks above remain stable.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [actions.openRecipient, controller.setView, location.pathname, location.search, sequence, updateActiveTab])

  if (controller.isLoading) return <div role="status" className="flex h-96 items-center justify-center gap-2 text-slate-500"><Loader2 className="h-6 w-6 animate-spin" />Loading claim…</div>
  if (controller.isPermissionDenied) return <ClaimPermissionDenied />
  if (controller.error || !controller.claim) return <div role="alert" className="flex h-96 flex-col items-center justify-center text-red-600"><AlertCircle className="h-10 w-10" /><p className="mt-2 font-medium">Claim could not be loaded.</p><button onClick={() => controller.retry()} className="mt-3 underline">Try again</button></div>

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900">
      <ClaimEditorHeader controller={controller} onBack={() => { if (!controller.hasUnsavedChanges || window.confirm('Leave before autosave completes?')) navigate('/claims') }} onDownload={actions.download} onSendClient={actions.openRecipient} />
      <main className="mx-auto max-w-7xl space-y-4 px-4 py-5 sm:px-6">
        <nav aria-label="Claim workspace" className="flex overflow-x-auto rounded-lg border bg-white p-1 dark:bg-slate-800">
          {(['valuation', 'assessment', 'evidence'] as const).map((view) => <button key={view} type="button" onClick={() => controller.setView(view)} aria-current={controller.view === view ? 'page' : undefined} className={`min-h-11 flex-1 rounded-md px-4 text-sm font-medium capitalize ${controller.view === view ? 'bg-amber-500 text-white' : 'text-slate-600'}`}>{view}</button>)}
        </nav>
        {controller.view === 'valuation' && <div className="grid gap-5 lg:grid-cols-[minmax(0,1fr)_20rem]"><ClaimLinesWorkspace controller={controller} /><ClaimSummary claim={controller.claim} /></div>}
        {controller.view === 'assessment' && <ClaimAssessmentWorkspace controller={controller} />}
        {controller.view === 'evidence' && <ClaimEvidencePanel evidence={evidence} editable={!controller.claim.snapshot_checksum} lines={controller.claim.lines.map((line) => ({ id: line.id, description: line.description }))} />}
      </main>
      <RecipientPickerDialog
        open={actions.recipientOpen}
        clientName={controller.claim.job.client_name ?? null}
        documentLabel={controller.claim.doc_type === 'invoice' ? 'Invoice' : 'Claim'}
        documentNumber={String(controller.claim.legal_number || sequence)}
        evidence={evidence.items}
        isSending={actions.sending}
        onClose={actions.closeRecipient}
        onSend={actions.sendToClient}
      />
    </div>
  )
}
